'use client';

import { md5 } from '@noble/hashes/legacy.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

const CONTROL_PREFIX = '/api/browser-upload-sessions/';
const TOKEN_HEADER = 'x-imai-upload-session-token';

export type BrowserUploadControl = { endpoint: string; token: string };

export function parseBrowserUploadControl(
  uploadUrl: string
): BrowserUploadControl | null {
  if (!uploadUrl.startsWith(CONTROL_PREFIX)) return null;
  const hashIndex = uploadUrl.indexOf('#');
  if (hashIndex < 0 || uploadUrl.indexOf('?', 0) >= 0) return null;
  const endpoint = uploadUrl.slice(0, hashIndex);
  const token = uploadUrl.slice(hashIndex + 1);
  const sessionId = endpoint.slice(CONTROL_PREFIX.length);
  if (!/^[a-z0-9]+$/i.test(sessionId) || !token || token.includes('#'))
    return null;
  return { endpoint, token };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export async function hashBrowserUploadBody(body: Blob | File) {
  const sha = sha256.create();
  const md = md5.create();
  const reader = body.stream().getReader();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    sha.update(value);
    md.update(value);
  }
  return {
    contentHash: `sha256:${bytesToHex(sha.digest())}`,
    contentMd5: base64(md.digest()),
    sizeBytes: body.size,
  };
}

/**
 * One id per upload call, sent with `begin` and `abort`. The server records
 * the attempt whose begin created the multipart upload and answers a replay
 * from any other attempt (a remount, a second caller) with `owner: false`;
 * only the owner may abort. `getRandomValues` works outside secure contexts.
 */
export function newBrowserUploadAttemptId(): string {
  return bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
}

/**
 * Whether a `begin` answer lets this attempt abort the session. A server
 * that predates ownership omits `owner`; every begin it answers is fresh.
 */
export function ownsBrowserUploadSession(begun: { owner?: unknown }): boolean {
  return begun.owner !== false;
}

/**
 * A replayed `begin` that lands while the first one is still creating the
 * multipart upload gets 409 BROWSER_UPLOAD_BEGIN_IN_PROGRESS; the server
 * finishes the first and answers a later replay from it, so wait and retry.
 */
export const BROWSER_UPLOAD_BEGIN_RETRY_DELAYS_MS = [300, 600, 1200];

function waitForBeginRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** POST one session control operation; retries `begin` while in progress. */
export async function fetchBrowserUploadControl(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(control.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [TOKEN_HEADER]: control.token,
      },
      body: JSON.stringify(body),
      signal,
    });
    const delay = BROWSER_UPLOAD_BEGIN_RETRY_DELAYS_MS[attempt];
    if (
      response.ok ||
      body.operation !== 'begin' ||
      response.status !== 409 ||
      delay === undefined
    )
      return response;
    const detail = await response
      .clone()
      .text()
      .catch(() => '');
    if (!detail.includes('BROWSER_UPLOAD_BEGIN_IN_PROGRESS')) return response;
    await waitForBeginRetry(delay, signal);
  }
}

export async function postBrowserUploadControl<T>(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal: AbortSignal
): Promise<T> {
  const response = await fetchBrowserUploadControl(control, body, signal);
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Upload session failed (${response.status}): ${detail}`);
  }
  return (await response.json()) as T;
}
